// src/pages/AboutPage.js
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    Target, Users, Brain, Heart, ArrowRight,
    Send, Handshake, FileText, MessageSquare,
    Building2, Briefcase, Megaphone
} from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';

const AboutPage = () => {
    const navigate = useNavigate();

    return (
        <motion.div
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
            className="min-h-screen pt-20"
        >
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-5xl mx-auto">
                        {/* Header */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-center mb-16"
                        >
                            <h1 className="text-4xl font-bold text-gray-900 mb-4">About Us</h1>
                        </motion.div>

                        {/* Our Mission */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="bg-white rounded-3xl p-8 shadow-lg mb-8"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Target className="h-8 w-8 text-blue-600" />
                                <h2 className="text-2xl font-bold text-gray-900">Our Mission</h2>
                            </div>
                            <p className="text-gray-700 leading-relaxed text-lg">
                                HousingAI exists to ensure fair and equal access to housing for all. We bridge the gap between
                                housing seekers and public housing authorities through innovative AI-powered solutions.
                            </p>
                        </motion.div>

                        {/* Who We Are */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="bg-white rounded-3xl p-8 shadow-lg mb-8"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Users className="h-8 w-8 text-green-600" />
                                <h2 className="text-2xl font-bold text-gray-900">Who We Are</h2>
                            </div>
                            <p className="text-gray-700 mb-6">
                                We are a dedicated team combining cutting-edge artificial intelligence technology with deep
                                housing policy expertise. Our leadership includes:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="bg-blue-50 rounded-xl p-6">
                                    <Brain className="h-10 w-10 text-blue-600 mb-3" />
                                    <h3 className="font-semibold mb-2">AI Technology Experts</h3>
                                    <p className="text-gray-600 text-sm">Specialists in artificial intelligence and machine learning applications</p>
                                </div>
                                <div className="bg-purple-50 rounded-xl p-6">
                                    <Building2 className="h-10 w-10 text-purple-600 mb-3" />
                                    <h3 className="font-semibold mb-2">Former PHA Staff</h3>
                                    <p className="text-gray-600 text-sm">Professionals with years of hands-on experience in public housing authorities</p>
                                </div>
                                <div className="bg-green-50 rounded-xl p-6">
                                    <Megaphone className="h-10 w-10 text-green-600 mb-3" />
                                    <h3 className="font-semibold mb-2">Housing Policy Advocates</h3>
                                    <p className="text-gray-600 text-sm">Committed to breaking down barriers in subsidized housing access</p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Why We Matter */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="bg-white rounded-3xl p-8 shadow-lg mb-8"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Heart className="h-8 w-8 text-purple-600" />
                                <h2 className="text-2xl font-bold text-gray-900">Why We Matter</h2>
                            </div>
                            <p className="text-gray-700 leading-relaxed">
                                Access to affordable housing shouldn't be complicated. We believe that combining comprehensive
                                PHA (Public Housing Authority) policies and program-specific knowledge with innovative AI
                                solutions is vital in addressing the complex challenges of subsidized housing.
                            </p>
                        </motion.div>

                        {/* Our Approach */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="bg-white rounded-3xl p-8 shadow-lg mb-8"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Briefcase className="h-8 w-8 text-indigo-600" />
                                <h2 className="text-2xl font-bold text-gray-900">Our Approach</h2>
                            </div>
                            <p className="text-gray-700 mb-6">We leverage technology to:</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                                    <div>
                                        <strong className="text-gray-900">Simplify Complex Processes:</strong>
                                        <span className="text-gray-700"> Transform bureaucratic procedures into user-friendly experiences</span>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                                    <div>
                                        <strong className="text-gray-900">Ensure Equal Access:</strong>
                                        <span className="text-gray-700"> Remove information barriers that prevent fair housing opportunities</span>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                                    <div>
                                        <strong className="text-gray-900">Provide Real-Time Assistance:</strong>
                                        <span className="text-gray-700"> Offer immediate guidance on housing policies and programs</span>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                                    <div>
                                        <strong className="text-gray-900">Bridge Knowledge Gaps:</strong>
                                        <span className="text-gray-700"> Connect tenants and housing seekers with the information they need, when they need it</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Our Commitment */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.7 }}
                            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 shadow-lg mb-16"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Handshake className="h-8 w-8 text-blue-600" />
                                <h2 className="text-2xl font-bold text-gray-900">Our Commitment</h2>
                            </div>
                            <p className="text-gray-700 leading-relaxed text-lg">
                                At HousingAI, we're not just building technology – we're building pathways to stable,
                                affordable homes. Every feature we develop, every policy we decode, and every barrier
                                we remove brings us closer to our vision of housing equity for all.
                            </p>
                        </motion.div>

                        {/* Services Section */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="mb-16"
                        >
                            <h2 className="text-3xl font-bold text-center mb-12">Services</h2>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                                    <MessageSquare className="h-12 w-12 text-blue-600 mb-4" />
                                    <h3 className="text-xl font-bold mb-3">AI-Powered Housing Navigation</h3>
                                    <ul className="space-y-2 text-gray-600">
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                                            <span>Personalized housing search assistance</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                                            <span>Eligibility screening and verification</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                                            <span>Application guidance and support</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                                            <span>Real-time policy updates and alerts</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                                    <Building2 className="h-12 w-12 text-green-600 mb-4" />
                                    <h3 className="text-xl font-bold mb-3">PHA Integration Platform</h3>
                                    <ul className="space-y-2 text-gray-600">
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-1.5"></div>
                                            <span>Direct connections to public housing authorities</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-1.5"></div>
                                            <span>Streamlined application processing</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-1.5"></div>
                                            <span>Document management and tracking</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-1.5"></div>
                                            <span>Status updates and notifications</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                                    <FileText className="h-12 w-12 text-purple-600 mb-4" />
                                    <h3 className="text-xl font-bold mb-3">Policy Intelligence</h3>
                                    <ul className="space-y-2 text-gray-600">
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-1.5"></div>
                                            <span>Comprehensive database of housing programs</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-1.5"></div>
                                            <span>AI-driven policy interpretation</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-1.5"></div>
                                            <span>Multilingual support</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-1.5"></div>
                                            <span>Accessibility features for all users</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </motion.div>

                        {/* Contact CTA */}
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.9 }}
                            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-12 text-center"
                        >
                            <Send className="h-16 w-16 mx-auto mb-6 text-blue-600" />
                            <h2 className="text-3xl font-bold mb-4 text-gray-900">Contact</h2>
                            <p className="text-xl mb-8 text-gray-700">
                                Ready to transform your housing search? Get in touch with our team today.
                            </p>
                            <button
                                onClick={() => navigate('/contact')}
                                className="bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-xl inline-flex items-center gap-2"
                            >
                                Get Started <ArrowRight className="h-5 w-5" />
                            </button>
                        </motion.div>
                    </div>
                </div>
            </section>
        </motion.div>
    );
};

export default AboutPage;

// ===== END OF src/pages/AboutPage.js =====